import { useContext, useEffect, useState } from 'react'
import { getEnvironment, getQuarterlySubscribers } from '../../api/api'
import { QuarterlySubscribersResponse } from '../../api/models'
import { AppStateContext } from '../../state/AppProvider'

const KR_REFRESH_INTERVAL_MS = 5 * 60 * 1000

const isFresh = (fetchedAt: number | null | undefined): boolean =>
  fetchedAt != null && Date.now() - fetchedAt < KR_REFRESH_INTERVAL_MS

export type QuarterlySubscribersState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; data: QuarterlySubscribersResponse }

export const useQuarterlySubscribers = (): QuarterlySubscribersState => {
  const appStateContext = useContext(AppStateContext)
  const dispatch = appStateContext?.dispatch
  const environment = getEnvironment()
  const isCachedForEnvironment = appStateContext?.state.krEnvironment === environment
  const cachedData = isCachedForEnvironment ? appStateContext.state.krData : null
  const cacheIsFresh = isCachedForEnvironment && isFresh(appStateContext?.state.krFetchedAt)
  const [failureMessage, setFailureMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!dispatch || cacheIsFresh) return
    const controller = new AbortController()

    getQuarterlySubscribers(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return
        setFailureMessage(null)
        dispatch({ type: 'SET_KR_DATA', payload: { data, environment, fetchedAt: Date.now() } })
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        setFailureMessage(err instanceof Error ? err.message : 'Failed to load quarterly subscriber KR')
      })

    return () => controller.abort()
  }, [dispatch, environment, cacheIsFresh])

  if (!dispatch) return { status: 'error', message: 'Quarterly subscriber data is unavailable.' }
  if (cachedData) return { status: 'loaded', data: cachedData }
  if (failureMessage) return { status: 'error', message: failureMessage }
  return { status: 'loading' }
}
